import { type ComponentType, useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";

import { EhrConnector, getMe } from "@elation/api-client/src/stitch-web";
import { useSyncPatientByEhr } from "@elation/api-client/src/stitch-web/stitchWeb";

import { IS_IN_IFRAME } from "@src/env";
import { BEARER_TOKEN_KEY, type Session } from "@src/features/auth";
import { appStore } from "@src/shared/stores/app";
import { toErrorMessage } from "@src/shared/utils/errors";
import {
  iframeLoginMethod,
  trackSessionInitFailure,
  trackUserLoggedIn,
} from "@src/shared/utils/observability/trackUserLoggedIn";

import {
  sendIframeEvent,
  useEhrFromIframe,
  useIframeLoginState,
  useIsIframeInitialized,
  usePatientFromIframe,
} from "./machine";

export type IframeLoginFlowDestinations = {
  home: () => string;
  patient: (patientId: string) => string;
};

export type IframeLoginViewProps = {
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
};

type IframeLoginFlowProps = {
  isLoading: boolean;
  error?: string | null;
  session: Session | null;
  destinations: IframeLoginFlowDestinations;
  view: ComponentType<IframeLoginViewProps>;
};

function trackIframeLogin(ehr: EhrConnector) {
  const method = iframeLoginMethod(ehr);
  getMe()
    .then(({ data: me }) => trackUserLoggedIn(me, method))
    .catch((err) => {
      console.error("Failed to record login analytics:", err);
      trackSessionInitFailure(method, err);
    });
}

export function IframeLoginFlow(props: IframeLoginFlowProps) {
  const {
    isLoading,
    error,
    session,
    destinations,
    view: IframeLoginView,
  } = props;

  const iframeState = useIframeLoginState();
  const patientFromIframe = usePatientFromIframe();
  const ehrFromIframe = useEhrFromIframe();

  const isIframeInitialized = useIsIframeInitialized();

  const syncPatient = useSyncPatientByEhr();
  const { mutate: syncMutate, isPending: isSyncLoading } = syncPatient;
  const isSyncingIframePatient = iframeState.matches("syncing");

  const firedForRef = useRef<string | null>(null);

  const handleRetry = () => {
    localStorage.removeItem(BEARER_TOKEN_KEY);
    window.location.reload();
  };

  useEffect(() => {
    appStore.getState().setPracticeTimezone(undefined);
  }, []);

  useEffect(
    function notifySessionState() {
      if (!IS_IN_IFRAME) return;

      if (session) {
        sendIframeEvent({ type: "SESSION_READY" });
      } else {
        sendIframeEvent({ type: "SESSION_MISSING" });
      }
    },
    [session],
  );

  useEffect(
    function triggerSyncOnce() {
      if (!isSyncingIframePatient) return;
      if (!ehrFromIframe || !patientFromIframe?.id) return;

      const fireKey = `${ehrFromIframe}:${patientFromIframe.id}`;

      if (firedForRef.current === fireKey) {
        return;
      }

      firedForRef.current = fireKey;

      syncMutate(
        {
          ehr: ehrFromIframe as EhrConnector,
          patientId: patientFromIframe.id.toString(),
        },
        {
          onSuccess: (data) => {
            sendIframeEvent({
              type: "SYNC_SUCCESS",
              patient: data.data.patient,
            });
          },
          onError: (err) => {
            sendIframeEvent({
              type: "SYNC_ERROR",
              error: toErrorMessage(err),
            });
          },
        },
      );
    },
    [isSyncingIframePatient, ehrFromIframe, patientFromIframe, syncMutate],
  );

  useEffect(
    function trackLoginOnInitialization() {
      if (!isIframeInitialized) return;
      if (!ehrFromIframe) return;

      trackIframeLogin(ehrFromIframe as EhrConnector);
    },
    [isIframeInitialized, ehrFromIframe],
  );

  if (iframeState.matches("redirectToPatients")) {
    return <Navigate to={destinations.home()} replace />;
  }

  if (iframeState.matches("redirectToBriefing")) {
    const stitchPatient = iframeState.context.stitchPatient;

    if (stitchPatient) {
      return (
        <Navigate
          to={destinations.patient(stitchPatient.id.toString())}
          replace
        />
      );
    }
  }

  if (iframeState.matches("error")) {
    return (
      <IframeLoginView
        isLoading={false}
        error={iframeState.context.error}
        onRetry={handleRetry}
      />
    );
  }

  if (iframeState.matches("initializing")) {
    return <IframeLoginView isLoading />;
  }

  const displayError = error ?? null;

  return (
    <IframeLoginView
      isLoading={isLoading || isSyncLoading}
      error={displayError}
      onRetry={displayError ? handleRetry : undefined}
    />
  );
}
